import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import BulkActionBar from "../../components/BulkActionBar";
import EvidenceReferenceField from "../../components/EvidenceReferenceField";
import FixedIsoClauseField from "../../components/FixedIsoClauseField";
import EvidenceReferenceView from "../../components/EvidenceReferenceView";
import {
  canRequestApprovalForItem as canRequestApprovalForRecord,
  isApprovalApproved,
  isApprovalPending,
  isApprovalPendingReview,
  requestBulkEmsApproval,
} from "../../lib/emsApproval";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/ControlledDocuments.css";
import {
  createProcedureRegisterItemApi,
  decideEmsApprovalApi,
  deleteProcedureRegisterItemApi,
  downloadProcedureRegisterCsvApi,
  fetchEmsApprovalPolicyApi,
  fetchEmsAuditLogApi,
  fetchProcedureVersionTimelineApi,
  fetchProcedureRegisterApi,
  requestEmsApprovalApi,
  updateProcedureRegisterItemApi,
  verifyEmsClosureApi,
  canApproveEmsModule,
} from "../../lib/api";

const MODULE_KEY = "controlled_documents";

const EMPTY_FORM = {
  iso_clause: "",
  title: "",
  doc_number: "",
  version: "",
  change_summary: "",
  owner: "",
  approver: "",
  approval_date: "",
  review_date: "",
  status: "draft",
  location: "",
  evidence: "",
  notes: "",
};

export default function ControlledDocuments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [exporting, setExporting] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOwner, setFilterOwner] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dueThisMonthOnly, setDueThisMonthOnly] = useState(false);
  const [isoClauseLabels, setIsoClauseLabels] = useState({});
  const [defaultIsoClause, setDefaultIsoClause] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [requestRoles, setRequestRoles] = useState([]);
  const [approveRoles, setApproveRoles] = useState([]);
  const [reviewRoles, setReviewRoles] = useState([]);
  const [verifyRoles, setVerifyRoles] = useState([]);
  const [workflowMode, setWorkflowMode] = useState("one_step");
  const [closureVerificationRequired, setClosureVerificationRequired] = useState(false);
  const [approvalBusyId, setApprovalBusyId] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRequesting, setBulkRequesting] = useState(false);
  const [historyItemId, setHistoryItemId] = useState("");
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [versionTimelineRows, setVersionTimelineRows] = useState([]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchProcedureRegisterApi();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load procedures register");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPolicy = async () => {
    try {
      const payload = await fetchEmsApprovalPolicyApi();
      const policyRows = Array.isArray(payload?.policy) ? payload.policy : [];
      const labels = payload?.iso_clause_labels && typeof payload.iso_clause_labels === "object"
        ? payload.iso_clause_labels
        : {};
      const modulePolicy = policyRows.find((r) => String(r?.module_key || "") === MODULE_KEY) || {};
      const options = Array.isArray(modulePolicy?.iso_clause_options)
        ? modulePolicy.iso_clause_options.map((x) => String(x))
        : [];
      const fallbackDefault = String(modulePolicy?.iso_clause_default || options[0] || "");
      const reqRoles = Array.isArray(modulePolicy?.request_roles) ? modulePolicy.request_roles.map((x) => String(x).toLowerCase()) : [];
      const appRoles = Array.isArray(modulePolicy?.approve_roles) ? modulePolicy.approve_roles.map((x) => String(x).toLowerCase()) : [];
      const revRoles = Array.isArray(modulePolicy?.review_roles) ? modulePolicy.review_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const verRoles = Array.isArray(modulePolicy?.verify_roles) ? modulePolicy.verify_roles.map((x) => String(x).toLowerCase()) : appRoles;
      const wfMode = String(modulePolicy?.approval_workflow_mode || "one_step").toLowerCase();
      setIsoClauseLabels(labels);
      setDefaultIsoClause(fallbackDefault);
      setRequestRoles(reqRoles);
      setApproveRoles(appRoles);
      setReviewRoles(revRoles);
      setVerifyRoles(verRoles);
      setWorkflowMode(wfMode === "two_step" ? "two_step" : "one_step");
      setClosureVerificationRequired(Boolean(modulePolicy?.closure_verification_required));
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || fallbackDefault }));
    } catch {
      setIsoClauseLabels({});
      setDefaultIsoClause("7");
      setRequestRoles(["client", "admin"]);
      setApproveRoles(["admin"]);
      setReviewRoles(["admin"]);
      setVerifyRoles(["admin"]);
      setWorkflowMode("one_step");
      setClosureVerificationRequired(false);
      setForm((prev) => ({ ...prev, iso_clause: prev.iso_clause || "7" }));
    }
  };

  useEffect(() => {
    loadItems();
    loadPolicy();
  }, []);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const resetForm = () => {
    setForm({ ...EMPTY_FORM, iso_clause: defaultIsoClause });
    setEditingId("");
    setSubmitAttempted(false);
  };

  const validationErrors = useMemo(() => {
    const errs = [];
    if (!String(form.title || "").trim()) errs.push("Title is required.");
    if (!String(form.doc_number || "").trim()) errs.push("Document number is required.");
    if (!String(form.iso_clause || "").trim()) errs.push("ISO clause is required.");
    if (String(form.status || "").trim().toLowerCase() === "approved") {
      if (!String(form.version || "").trim()) errs.push("Version is required before setting status to approved.");
      if (!String(form.owner || "").trim()) errs.push("Owner is required before setting status to approved.");
      if (!String(form.approver || "").trim()) errs.push("Approver is required before setting status to approved.");
      if (!String(form.approval_date || "").trim()) errs.push("Approval date is required before setting status to approved.");
      if (!String(form.review_date || "").trim()) errs.push("Review date is required before setting status to approved.");
      if (!String(form.location || "").trim()) errs.push("Location/link is required before setting status to approved.");
      if (!String(form.evidence || "").trim()) errs.push("Evidence is required before setting status to approved.");
    }
    return errs;
  }, [form]);

  const statusBadgeClass = (status) => `badge badge-status-${String(status || "").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const approvalBadgeClass = (status) => `badge badge-approval-${String(status || "not_requested").trim().toLowerCase().replace(/\s+/g, "_")}`;
  const userRole = String(localStorage.getItem("role") || "").trim().toLowerCase();
  const canRequest = requestRoles.includes(userRole);
  const canApprove = approveRoles.includes(userRole) && canApproveEmsModule("controlled_documents");
  const canReview = reviewRoles.includes(userRole);
  const canVerify = verifyRoles.includes(userRole);
  const completionStatuses = useMemo(() => ["approved"], []);
  const canRequestApprovalForItem = useCallback((item) =>
    canRequestApprovalForRecord(item, canRequest, completionStatuses),
    [canRequest, completionStatuses]
  );
  const isCompletionStatus = (status) => completionStatuses.includes(String(status || "").trim().toLowerCase());

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      iso_clause: item.iso_clause || defaultIsoClause,
      title: item.title || "",
      doc_number: item.doc_number || "",
      version: item.version || "",
      change_summary: item.change_summary || "",
      owner: item.owner || "",
      approver: item.approver || "",
      approval_date: item.approval_date || "",
      review_date: item.review_date || "",
      status: item.status || "draft",
      location: item.location || "",
      evidence: item.evidence || "",
      notes: item.notes || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (validationErrors.length) {
      toast.error(validationErrors[0]);
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await updateProcedureRegisterItemApi(editingId, form);
        toast.success("Procedure updated");
      } else {
        await createProcedureRegisterItemApi(form);
        toast.success("Procedure created");
      }
      resetForm();
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to save procedure");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this controlled document entry?");
    if (!ok) return;
    try {
      setDeletingId(id);
      await deleteProcedureRegisterItemApi(id);
      toast.success("Procedure deleted");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to delete procedure");
    } finally {
      setDeletingId("");
    }
  };

  const handleRequestApproval = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional approval request note:", "") || "";
      await requestEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Approval requested");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to request approval");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleDecision = async (item, decision) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt(`Optional note for ${decision}:`, "") || "";
      await decideEmsApprovalApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        decision,
        notes,
      });
      const msg = decision === "review" ? "Item reviewed" : decision === "approve" ? "Item approved" : "Item rejected";
      toast.success(msg);
      await loadItems();
    } catch (err) {
      toast.error(err?.message || `Failed to ${decision}`);
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleVerifyClose = async (item) => {
    try {
      setApprovalBusyId(item.id);
      const notes = window.prompt("Optional closure verification note:", "") || "";
      await verifyEmsClosureApi({
        module_key: MODULE_KEY,
        item_id: item.id,
        notes,
      });
      toast.success("Closure verified");
      await loadItems();
    } catch (err) {
      toast.error(err?.message || "Failed to verify closure");
    } finally {
      setApprovalBusyId("");
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const blob = await downloadProcedureRegisterCsvApi();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "procedures_register.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("Procedures register exported");
    } catch (err) {
      toast.error(err?.message || "Failed to export procedures register");
    } finally {
      setExporting(false);
    }
  };

  const openItemHistory = async (item) => {
    try {
      setHistoryLoading(true);
      setHistoryItemId(String(item?.id || ""));
      const entries = await fetchEmsAuditLogApi({ module_key: MODULE_KEY });
      const timelinePayload = await fetchProcedureVersionTimelineApi(item?.id);
      const rows = (Array.isArray(entries) ? entries : [])
        .filter((e) => String(e?.item_id || "") === String(item?.id || ""))
        .sort((a, b) => String(b?.timestamp || "").localeCompare(String(a?.timestamp || "")));
      setHistoryRows(rows);
      setVersionTimelineRows(Array.isArray(timelinePayload?.timeline) ? timelinePayload.timeline : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load document history");
      setHistoryRows([]);
      setVersionTimelineRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const ownerOptions = useMemo(() => {
    const set = new Set(
      (items || [])
        .map((it) => String(it.owner || "").trim())
        .filter(Boolean)
    );
    return Array.from(set).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const ym = new Date().toISOString().slice(0, 7);
    return (items || []).filter((it) => {
      if (filterStatus !== "all" && String(it.status || "") !== filterStatus) return false;
      if (filterOwner === "__unassigned__") {
        if (String(it.owner || "").trim()) return false;
      } else if (filterOwner !== "all" && String(it.owner || "") !== filterOwner) {
        return false;
      }
      if (overdueOnly) {
        const rd = String(it.review_date || "").trim();
        if (!rd || rd >= today) return false;
      }
      if (dueThisMonthOnly) {
        const rd = String(it.review_date || "").trim();
        if (!rd.startsWith(ym)) return false;
      }
      return true;
    });
  }, [items, filterStatus, filterOwner, overdueOnly, dueThisMonthOnly]);

  const selectableFilteredIds = useMemo(
    () => filteredItems.filter((it) => canRequestApprovalForItem(it)).map((it) => String(it.id)),
    [filteredItems, canRequestApprovalForItem]
  );
  const selectedSet = useMemo(() => new Set(selectedIds.map((x) => String(x))), [selectedIds]);
  const allSelectableSelected = selectableFilteredIds.length > 0 && selectableFilteredIds.every((id) => selectedSet.has(String(id)));
  const selectedEligibleItems = useMemo(
    () => filteredItems.filter((it) => selectedSet.has(String(it.id)) && canRequestApprovalForItem(it)),
    [filteredItems, selectedSet, canRequestApprovalForItem]
  );

  useEffect(() => {
    const liveIds = new Set((items || []).map((it) => String(it.id)));
    setSelectedIds((prev) => prev.filter((id) => liveIds.has(String(id))));
  }, [items]);

  const applyPreset = (preset) => {
    if (preset === "draft_only") {
      setFilterStatus("draft");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "in_review_only") {
      setFilterStatus("in_review");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(false);
      return;
    }
    if (preset === "due_this_month") {
      setFilterStatus("all");
      setFilterOwner("all");
      setOverdueOnly(false);
      setDueThisMonthOnly(true);
      return;
    }
    setFilterStatus("all");
    setFilterOwner("all");
    setOverdueOnly(false);
    setDueThisMonthOnly(false);
  };

  const toggleSelectOne = (itemId, checked) => {
    const id = String(itemId);
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      if (checked) next.add(id);
      else next.delete(id);
      return Array.from(next);
    });
  };

  const toggleSelectAllFiltered = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev.map((x) => String(x)));
      for (const id of selectableFilteredIds) {
        if (checked) next.add(String(id));
        else next.delete(String(id));
      }
      return Array.from(next);
    });
  };

  const handleBulkRequestApproval = async () => {
    if (!selectedEligibleItems.length) {
      toast.error("Select at least one eligible row");
      return;
    }
    const notes = window.prompt("Optional bulk approval request note:", "") || "";
    try {
      setBulkRequesting(true);
      const { ok, failed } = await requestBulkEmsApproval(
        requestEmsApprovalApi,
        MODULE_KEY,
        selectedEligibleItems,
        notes
      );
      if (ok > 0) toast.success(`Requested approval for ${ok} item(s)`);
      if (failed > 0) toast.error(`${failed} item(s) failed`);
      setSelectedIds([]);
      await loadItems();
    } finally {
      setBulkRequesting(false);
    }
  };

  return (
    <div className="dashboard-page controlled-documents-page">
      <div className="dashboard-header">
        <div>
          <p className="controlled-documents-eyebrow">ISO 14001 support</p>
          <h1>Controlled Documents</h1>
          <p className="subtitle">Maintain a clear record of current policies, procedures, forms and work instructions, including their version, ownership, approval and review cycle.</p>
        </div>
      </div>

      <section className="card controlled-documents-guidance">
        <div>
          <p className="controlled-documents-eyebrow">How to complete a document record</p>
          <h3>Identify the document, confirm its current version, then manage approval and review</h3>
          <p>Use this register for documents that need controlled ownership and version history. Keep only the approved version in use and retain previous versions where your process requires it.</p>
        </div>
        <div className="controlled-documents-guidance-grid">
          <div><strong>1. Identify it</strong><span>Add a clear title and unique document number so people can find the current record.</span></div>
          <div><strong>2. Control changes</strong><span>Record the version and a short summary of what changed whenever the document is updated.</span></div>
          <div><strong>3. Approve and review</strong><span>Assign an owner, approver, evidence location and next review date.</span></div>
        </div>
      </section>

      <div className="card settings-card controlled-documents-form">
        <div className="settings-header">
          <div>
            <p className="controlled-documents-eyebrow">Your document record</p>
            <h3>{editingId ? "Edit Document" : "Add Document"}</h3>
          </div>
          <span className="kpi-sub">Record the current version, responsible people, approved location and next review.</span>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="settings-grid">
            <FixedIsoClauseField clause={defaultIsoClause} labels={isoClauseLabels} />
            <label className="settings-field">
              <span>Title</span>
              <input placeholder="e.g. Environmental Policy" value={form.title} onChange={(e) => setField("title", e.target.value)} disabled={saving} />
              <small>Use the document's formal title as it appears on the approved document.</small>
            </label>
            <label className="settings-field">
              <span>Document number</span>
              <input placeholder="e.g. EMS-POL-001" value={form.doc_number} onChange={(e) => setField("doc_number", e.target.value)} disabled={saving} />
              <small>Use a unique, consistent reference that stays with the document through future versions.</small>
            </label>
            <label className="settings-field">
              <span>Version</span>
              <input placeholder="e.g. 1.0" value={form.version} onChange={(e) => setField("version", e.target.value)} disabled={saving} />
              <small>Start at 1.0. Use 1.1 for a minor update or 2.0 for a material approved change.</small>
            </label>
            <label className="settings-field controlled-documents-wide-field">
              <span>Version change summary</span>
              <input placeholder="e.g. Added annual energy-review responsibilities and updated the approval route." value={form.change_summary} onChange={(e) => setField("change_summary", e.target.value)} disabled={saving} />
              <small>Summarise what changed from the previous version so reviewers can understand the update.</small>
            </label>
            <label className="settings-field">
              <span>Status</span>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} disabled={saving}>
                <option value="draft">draft</option>
                <option value="in_review">in_review</option>
                <option value="approved">approved</option>
                <option value="obsolete">obsolete</option>
              </select>
              <small>Use draft while being prepared, in review while awaiting sign-off, approved when current, and obsolete when replaced.</small>
            </label>
            <label className="settings-field">
              <span>Owner</span>
              <input placeholder="e.g. Sustainability Lead" value={form.owner} onChange={(e) => setField("owner", e.target.value)} disabled={saving} />
              <small>Name the person or role responsible for keeping the document current.</small>
            </label>
            <label className="settings-field">
              <span>Approver</span>
              <input placeholder="e.g. Managing Director" value={form.approver} onChange={(e) => setField("approver", e.target.value)} disabled={saving} />
              <small>Name the authorised person or role that approves this document.</small>
            </label>
            <label className="settings-field">
              <span>Approval date</span>
              <input type="date" value={form.approval_date} onChange={(e) => setField("approval_date", e.target.value)} disabled={saving} />
              <small>Enter the date the current version was formally approved.</small>
            </label>
            <label className="settings-field">
              <span>Review date</span>
              <input type="date" value={form.review_date} onChange={(e) => setField("review_date", e.target.value)} disabled={saving} />
              <small>Set the next planned review date, and bring it forward after a relevant change.</small>
            </label>
            <label className="settings-field controlled-documents-wide-field">
              <span>Location / link</span>
              <input placeholder="e.g. https://.../environmental-policy-v1.0.pdf or document:environmental_policy.pdf" value={form.location} onChange={(e) => setField("location", e.target.value)} disabled={saving} />
              <small>Link to the approved, accessible current version in your document library or on the platform.</small>
            </label>
            <EvidenceReferenceField
              label="Evidence / link"
              value={form.evidence}
              onChange={(value) => setField("evidence", value)}
              disabled={saving}
              helpText="Link the signed document, approval record, consultation notes, or other evidence supporting the document lifecycle."
            />
            <label className="settings-field controlled-documents-wide-field">
              <span>Notes</span>
              <input placeholder="Optional: record distribution, staff communication, retained previous version, or follow-up actions." value={form.notes} onChange={(e) => setField("notes", e.target.value)} disabled={saving} />
              <small>Use notes to capture information that helps demonstrate document control at audit.</small>
            </label>
          </div>
          {submitAttempted && validationErrors.length > 0 ? (
            <div className="form-error-summary">
              Please fix the following before saving:
              <ul>
                {validationErrors.map((msg) => <li key={msg}>{msg}</li>)}
              </ul>
            </div>
          ) : null}
          <div className="settings-actions">
            {editingId ? (
              <button type="button" className="btn-secondary" onClick={resetForm} disabled={saving} style={{ marginRight: 10 }}>
                Cancel
              </button>
            ) : null}
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update document" : "Add document"}
            </button>
          </div>
        </form>
      </div>

      <BulkActionBar
        selectedCount={selectedIds.length}
        eligibleCount={selectedEligibleItems.length}
        onClear={() => setSelectedIds([])}
        onRequest={handleBulkRequestApproval}
        requesting={bulkRequesting}
        requestDisabled={bulkRequesting || selectedEligibleItems.length === 0}
        onExport={handleExportCsv}
        exporting={exporting}
      />

      <div className="filter-bar" style={{ gridTemplateColumns: "180px 180px auto 1fr" }}>
        <label className="settings-field">
          <span>Status</span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">all</option>
            <option value="draft">draft</option>
            <option value="in_review">in_review</option>
            <option value="approved">approved</option>
            <option value="obsolete">obsolete</option>
          </select>
        </label>

        <label className="settings-field">
          <span>Owner</span>
          <select value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)}>
            <option value="all">all</option>
            <option value="__unassigned__">unassigned</option>
            {ownerOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </label>

        <label className="checkbox-field">
          <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} />
          Overdue review only
        </label>

        <span className="filter-count">{filteredItems.length} of {items.length} shown</span>
      </div>

      <div className="preset-bar">
        <button className="btn-secondary btn-small" onClick={() => applyPreset("draft_only")}>Only draft</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("in_review_only")}>Only in review</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("due_this_month")}>Review due this month</button>
        <button className="btn-secondary btn-small" onClick={() => applyPreset("clear")}>Clear presets</button>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allSelectableSelected}
                  onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                  disabled={!selectableFilteredIds.length}
                />
              </th>
              <th>ISO Clause</th>
              <th>Title</th>
              <th>Doc #</th>
              <th>Version</th>
              <th>Status</th>
              <th>Approval</th>
              <th>Owner</th>
              <th>Review date</th>
              <th>Evidence</th>
              <th>Updated by</th>
              <th>Updated at</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={13} className="table-empty">Loading...</td></tr>
            ) : filteredItems.length === 0 ? (
              <tr><td colSpan={13} className="table-empty">No controlled documents yet.</td></tr>
            ) : (
              filteredItems.map((it) => (
                <tr key={it.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedSet.has(String(it.id))}
                      onChange={(e) => toggleSelectOne(it.id, e.target.checked)}
                      disabled={!canRequestApprovalForItem(it)}
                    />
                  </td>
                  <td>{it.iso_clause ? (isoClauseLabels[it.iso_clause] ? `${it.iso_clause} - ${isoClauseLabels[it.iso_clause]}` : `Clause ${it.iso_clause}`) : "-"}</td>
                  <td>{it.title || "-"}</td>
                  <td>{it.doc_number || "-"}</td>
                  <td>{it.version || "-"}</td>
                  <td><span className={statusBadgeClass(it.status)}>{it.status || "-"}</span></td>
                  <td><span className={approvalBadgeClass(it.approval_status)}>{it.approval_status || "not_requested"}</span></td>
                  <td>{it.owner || "-"}</td>
                  <td>{it.review_date || "-"}</td>
                  <td><EvidenceReferenceView value={it.evidence} /></td>
                  <td>{it.updated_by || "-"}</td>
                  <td>{it.updated_at || "-"}</td>
                  <td>
                    <button className="admin-btn secondary" onClick={() => startEdit(it)} style={{ marginRight: 8 }}>Edit</button>
                    <button className="admin-btn secondary" onClick={() => openItemHistory(it)} style={{ marginRight: 8 }}>
                      History
                    </button>
                    {canRequestApprovalForItem(it) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleRequestApproval(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Request"}
                      </button>
                    ) : null}
                    {workflowMode === "two_step" && canReview && isApprovalPendingReview(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "review")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Review"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {canApprove && isApprovalPending(it) ? (
                      <>
                        <button
                          className="admin-btn"
                          onClick={() => handleDecision(it, "approve")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Approve"}
                        </button>
                        <button
                          className="admin-btn secondary"
                          onClick={() => handleDecision(it, "reject")}
                          disabled={approvalBusyId === it.id}
                          style={{ marginRight: 8 }}
                        >
                          {approvalBusyId === it.id ? "Working..." : "Reject"}
                        </button>
                      </>
                    ) : null}
                    {closureVerificationRequired
                    && canVerify
                    && isCompletionStatus(it.status)
                    && isApprovalApproved(it)
                    && !Boolean(it.closure_verified) ? (
                      <button
                        className="admin-btn"
                        onClick={() => handleVerifyClose(it)}
                        disabled={approvalBusyId === it.id}
                        style={{ marginRight: 8 }}
                      >
                        {approvalBusyId === it.id ? "Working..." : "Verify close"}
                      </button>
                    ) : null}
                    <button className="admin-btn danger" onClick={() => handleDelete(it.id)} disabled={deletingId === it.id}>
                      {deletingId === it.id ? "Deleting..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {historyItemId ? (
        <div className="table-card" style={{ marginTop: 20 }}>
          <div className="dashboard-header" style={{ marginBottom: 10 }}>
            <div>
              <h3 style={{ margin: 0 }}>Document History</h3>
              <p className="subtitle" style={{ margin: 0 }}>Item ID: {historyItemId}</p>
            </div>
            <div className="dashboard-header-actions">
              <button className="btn-secondary" onClick={() => { setHistoryItemId(""); setHistoryRows([]); setVersionTimelineRows([]); }}>
                Close
              </button>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {historyLoading ? (
                <tr><td colSpan={4} className="table-empty">Loading history...</td></tr>
              ) : historyRows.length === 0 ? (
                <tr><td colSpan={4} className="table-empty">No history entries for this item yet.</td></tr>
              ) : (
                historyRows.map((e) => (
                  <tr key={e.id || `${e.timestamp}-${e.action}`}>
                    <td>{e.timestamp || "-"}</td>
                    <td>{e.actor || "-"}</td>
                    <td>{e.action || "-"}</td>
                    <td>{typeof e.details === "object" ? JSON.stringify(e.details) : String(e.details || "-")}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div style={{ marginTop: 12 }}>
            <h4 style={{ margin: "0 0 8px 0" }}>Version Timeline</h4>
            <table>
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Actor</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Summary</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr><td colSpan={5} className="table-empty">Loading version timeline...</td></tr>
                ) : versionTimelineRows.length === 0 ? (
                  <tr><td colSpan={5} className="table-empty">No version timeline events yet.</td></tr>
                ) : (
                  versionTimelineRows.map((r) => (
                    <tr key={String(r.event_id || `${r.timestamp}-${r.to_version}`)}>
                      <td>{r.timestamp || "-"}</td>
                      <td>{r.actor || "-"}</td>
                      <td>{r.from_version || "-"}</td>
                      <td>{r.to_version || "-"}</td>
                      <td>{r.change_summary || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}



